"use client";

import { EXECUTIVE_RECRUITERS } from "@/lib/demos/hiringData";
import { Star, ShieldCheck, Award, Clock, Users } from "lucide-react";

export function HiringTrustSection() {
  return (
    <section className="py-20 bg-gradient-to-b from-[#0a0710] to-[#0d0914] border-t border-purple-950/60 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-16">
        {/* Top Header */}
        <div className="text-center max-w-3xl mx-auto space-y-3">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-purple-500/10 border border-purple-500/20 text-purple-300 text-xs font-semibold">
            <Users className="w-3.5 h-3.5" />
            <span>Search Leadership &amp; Governance</span>
          </div>
          <h2 className="text-2xl sm:text-4xl font-extrabold tracking-tight">
            Managing Partners &amp; Practice Heads
          </h2>
          <p className="text-slate-400 text-sm sm:text-base">
            Every candidate is vetted by seasoned search partners who have built founding engineering teams, closed CXO mandates, and scaled global enterprises.
          </p>
        </div>

        {/* Recruiter Roster */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {EXECUTIVE_RECRUITERS.map((recruiter) => (
            <div
              key={recruiter.id}
              className="p-6 rounded-2xl bg-[#140c21]/90 border border-purple-900/40 space-y-4 hover:border-purple-600/50 transition-all shadow-lg"
            >
              <div className="flex items-center justify-between">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center font-bold text-white text-sm shadow-md shadow-purple-500/20">
                  {recruiter.name
                    .split(" ")
                    .map((n) => n[0])
                    .join("")}
                </div>
                <div className="flex items-center gap-1 text-xs text-amber-400 font-bold bg-amber-500/10 px-2 py-0.5 rounded border border-amber-500/20">
                  <Star className="w-3 h-3 fill-amber-400" />
                  <span>{recruiter.rating}</span>
                </div>
              </div>

              <div>
                <h4 className="font-bold text-white text-base">{recruiter.name}</h4>
                <p className="text-xs text-purple-400 font-medium">{recruiter.role}</p>
              </div>

              <div className="grid grid-cols-2 gap-2 pt-3 border-t border-purple-950/60 text-xs">
                <div>
                  <div className="text-[10px] text-slate-500">Placements YTD</div>
                  <div className="font-bold text-white text-sm">{recruiter.placementsYtd} CXO/Staff+</div>
                </div>
                <div>
                  <div className="text-[10px] text-slate-500">Avg Closure</div>
                  <div className="font-bold text-emerald-400 text-sm">{recruiter.avgClosureDays} Days</div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Search Architecture & SLA Guarantee Grid */}
        <div className="p-8 rounded-2xl bg-[#160f24] border border-purple-800/40 relative overflow-hidden">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 relative z-10">
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-purple-300 font-bold text-sm">
                <ShieldCheck className="w-4 h-4 text-emerald-400" />
                <span>180-Day Executive Warranty</span>
              </div>
              <p className="text-xs text-slate-400 leading-relaxed">
                If an executive resigns or fails to meet defined milestones within the guarantee period, Nexus conducts a comprehensive priority replacement at zero additional fee.
              </p>
            </div>

            <div className="space-y-2">
              <div className="flex items-center gap-2 text-purple-300 font-bold text-sm">
                <Clock className="w-4 h-4 text-indigo-400" />
                <span>14-Day Initial Dossier SLA</span>
              </div>
              <p className="text-xs text-slate-400 leading-relaxed">
                Receive the first calibrated shortlist of 3 to 5 thoroughly evaluated, pre-vetted senior candidates within 14 calendar days of search mandate execution.
              </p>
            </div>

            <div className="space-y-2">
              <div className="flex items-center gap-2 text-purple-300 font-bold text-sm">
                <Award className="w-4 h-4 text-fuchsia-400" />
                <span>Confidential Off-Market Mapping</span>
              </div>
              <p className="text-xs text-slate-400 leading-relaxed">
                Over 82% of our executive placements are actively employed leaders sourced through confidential discrete headhunting rather than job boards.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
